import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineChevronRight } from "react-icons/hi2";
import { cn } from "../../../lib/cn";
import Pagination from "../../shared/Pagination";

const STATUS_STYLES = {
  ACTIVE: { bg: "bg-secondary/10", text: "text-secondary-dark", label: "in progress" },
  PENDING: { bg: "bg-surface-hover", text: "text-grey", label: "planning" },
  COMPLETED: { bg: "bg-success/10", text: "text-success", label: "completed" },
  CANCELLED: { bg: "bg-danger/10", text: "text-danger", label: "cancelled" },
};

const PAGE_SIZE = 5;

const ActiveProjects = ({ projects = [], isLoading = false }) => {
  const navigate = useNavigate();

  const activeProjects = useMemo(
    () => projects.filter((p) => p.status === "ACTIVE" || p.status === "PENDING"),
    [projects]
  );

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(activeProjects.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const startIdx = (page - 1) * PAGE_SIZE;
  const visibleProjects = activeProjects.slice(startIdx, startIdx + PAGE_SIZE);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-surface-border">
        <h3 className="text-lg font-semibold text-primary mb-4">
          Active Projects
        </h3>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2 motion-safe:animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-surface-disabled rounded w-3/4 mb-1.5" />
                  <div className="h-3 bg-surface-disabled rounded w-1/3" />
                </div>
                <div className="h-5 w-16 bg-surface-disabled rounded-md" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-2 bg-surface-disabled rounded-full flex-1" />
                <div className="h-3 w-8 bg-surface-disabled rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-surface-border">
      <h3 className="text-lg font-semibold text-primary mb-4">
        Active Projects
      </h3>
      {activeProjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 bg-surface-disabled rounded-full flex items-center justify-center mb-3">
            <HiOutlineChevronRight className="w-6 h-6 text-grey" />
          </div>
          <p className="text-sm font-medium text-primary">No active projects</p>
          <p className="text-xs text-grey mt-1">
            Create a project to start tracking progress.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleProjects.map((project) => {
            const statusStyle = STATUS_STYLES[project.status] || STATUS_STYLES.PENDING;
            const progress = project.completion_percentage || 0;
            const ownerName = project.owner?.name;

            return (
              <button
                key={project.id}
                aria-label={`Open project ${project.title || "project"}`}
                onClick={() => navigate(`/projects/${project.id}/tasks`)}
                className="w-full text-left space-y-2 p-2 -m-2 rounded-lg hover:bg-surface-hover transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-secondary focus-visible:outline-none cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-darkgrey truncate">
                      {project.title}
                    </p>
                    {ownerName && (
                      <p className="text-xs text-mediumgrey mt-0.5">
                        {ownerName}
                      </p>
                    )}
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap min-h-[22px]",
                      statusStyle.bg,
                      statusStyle.text
                    )}
                  >
                    {statusStyle.label}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-full overflow-hidden rounded-full bg-secondary/10 h-2 flex-1">
                    <div
                      className="h-full bg-secondary rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-grey w-10 text-right">
                    {progress}%
                  </span>
                </div>
              </button>
            );
          })}
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={activeProjects.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};

export default ActiveProjects;
